export type CapabilityStatus = "Available" | "Pilot" | "Planned";

export const productDefinition =
  "Agentic Plus is an evidence-led creative growth platform. A scoped Growth Agent connects market evidence, brand memory, creative strategy, governed production, approvals, and learning in one auditable mission.";

export const productModules: {
  title: string;
  description: string;
  points: string[];
  status: CapabilityStatus;
}[] = [
  {
    title: "Growth Agent",
    description:
      "The Growth Agent turns a scoped objective into a reviewable plan across evidence, strategy, production, approval, delivery, and learning.",
    points: [
      "Carry workspace, product, and campaign context",
      "Prepare a plan instead of opening a blank chat",
      "Show the next action and the evidence behind it",
    ],
    status: "Pilot",
  },
  {
    title: "Growth Mission",
    description:
      "A Growth Mission captures the objective, product, audience, constraints, evidence, creative output, approvals, and result in one auditable place.",
    points: [
      "Start from a URL, goal, brief, or known creative problem",
      "Keep inputs and assumptions with the resulting assets",
      "Show current state and next responsible action",
    ],
    status: "Available",
  },
  {
    title: "Evidence Graph",
    description:
      "The Evidence Graph organizes product facts, brand guidance, historical creative, and authorized market signals with source and time context.",
    points: [
      "Separate verified facts from model inference",
      "Make the basis for a recommendation reviewable",
      "Avoid using unauthorized or unverifiable evidence",
    ],
    status: "Available",
  },
  {
    title: "Brand Brain",
    description:
      "Brand Brain is persistent brand memory: voice, audience, product facts, approved claims, constraints, and preferred patterns.",
    points: [
      "Reuse approved brand context across briefs",
      "Keep product truth separate from creative interpretation",
      "Make changes reviewable and versioned",
    ],
    status: "Available",
  },
  {
    title: "Creative Intelligence",
    description:
      "Creative Intelligence organizes authorized market inputs and historical creative into comparable hooks, offers, formats, and patterns.",
    points: [
      "Compare market patterns with brand history",
      "Identify gaps that justify a new test",
      "Keep source, time, and evidence level visible",
    ],
    status: "Available",
  },
  {
    title: "Creative Production",
    description:
      "Production creates copy, images, and standard video from an approved brief while retaining the model, prompt context, and version lineage.",
    points: [
      "Generate multiple candidate directions",
      "Preserve the brief and source references",
      "Download approved versions in usable formats",
    ],
    status: "Available",
  },
  {
    title: "Approval & Governance",
    description:
      "Approval boundaries make cost, context, and external consequences visible before a decision is applied.",
    points: [
      "Confirm high-cost generation before it runs",
      "Review assets, destination, and execution plan",
      "Record approval and outcome for future audit",
    ],
    status: "Available",
  },
  {
    title: "Experiment Memory & Decision Journal",
    description:
      "Experiment Memory connects a hypothesis to its brief, variants, approvals, results, and the decision that follows.",
    points: [
      "Preserve fixed, varied, and forbidden changes",
      "Record why a direction was approved or stopped",
      "Carry validated context into the next mission",
    ],
    status: "Pilot",
  },
  {
    title: "Governed Distribution",
    description:
      "Governed Distribution is designed to preview an external action, enforce permissions, read back the result, and preserve a compensation path.",
    points: [
      "Create a reviewable Dry Run before execution",
      "Separate upload, PAUSED, and ACTIVE permissions",
      "Read back provider state and plan undo where supported",
    ],
    status: "Pilot",
  },
];

export const productRoadmap = [
  {
    title: "URL-to-Mission enrichment",
    description:
      "Turn a product URL into a reviewable mission seed with candidate facts, landing-page signals, evidence gaps, and suggested next inputs.",
  },
  {
    title: "Creative Fatigue Detection",
    description:
      "Use authorized spend, frequency, click, and conversion trends to propose fatigue signals and replacement hypotheses.",
  },
  {
    title: "Landing Page Intelligence",
    description:
      "Check creative against PDP or landing-page product, offer, price, claim, and CTA before spend continues.",
  },
  {
    title: "Competitor Watch",
    description:
      "Monitor authorized competitor signals and convert meaningful market changes into evidence-backed test opportunities.",
  },
  {
    title: "MCP access",
    description:
      "Allow approved development hosts to call scoped Agentic Plus capabilities with the same permission, approval, and audit model.",
  },
  {
    title: "Broader performance learning",
    description:
      "Connect more authorized performance sources for post-mortems, experiment memory, and creative planning.",
  },
];

export const productFacts = [
  { term: "Primary users", detail: "Brand growth teams, agencies, and media buyers" },
  { term: "Deployment model", detail: "Multi-tenant web platform with scoped workspaces" },
  {
    term: "Production scope",
    detail: "Briefs, copy, images, and standard video from approved context",
  },
  {
    term: "Evidence model",
    detail: "Source, time, evidence level, verification state, and model inference stay separate",
  },
  {
    term: "Control model",
    detail: "Scoped context, approval, version lineage, audit, and compensation paths",
  },
  {
    term: "Status labels",
    detail: "Available is current scope; Pilot is scoped early access; Planned is not launched",
  },
];
